import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PHONE_PREFIXES } from "@/constants/phone";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Upload, Trash2, ImageIcon } from "lucide-react";
import { ProfileImageCropper } from "../onboarding/ProfileImageCropper";
import { useToast } from "@/hooks/use-toast";
import { getStoredUtmParams, clearUtmParams, getUtmParamsFromCookies } from "@/utils/utmUtils";
import { currencyMap, COUNTRIES } from "@/constants/currencies";

// Profile interface to include telegram_chat_id
interface Profile {
  first_name?: string | null;
  last_name?: string | null;
  profession?: string | null;
  preferred_messaging?: string | null;
  telegram_chat_id?: number | null;
  country?: string | null;
  whatsapp_number?: string | null;
  telegram_number?: string | null;
  currency?: string | null;
  profile_image_url?: string | null;
}

// List of professions
const PROFESSIONS = [
  "Hairdresser / Hairstylist",
  "Nail Technician",
  "Makeup Artist",
  "Personal Trainer",
  "Massage Therapist",
  "Photographer",
  "Graphic Designer",
  "Social Media Manager",
  "Barber",
  "Videographer",
  "Coach",
  "Accountant",
  "MC (Master of Ceremonies)",
  "Moderator",
  "Other"
] as const;

// List of common Amharic name patterns to detect Ethiopian names
const AMHARIC_NAME_PATTERNS = [
  "abebe", "alemu", "alemayehu", "bekele", "berhanu", "dawit", "desalegn", 
  "ephrem", "eshetu", "fekadu", "getachew", "girma", "haile", "kebede", 
  "kinfe", "melaku", "mekonnen", "melesse", "mengistu", "mulugeta", 
  "solomon", "tadesse", "tefera", "tekle", "teshome", "tilahun", "worku", "yohannes",
  
  "abebech", "almaz", "aregash", "aster", "belaynesh", "bertukan", "bezawit", 
  "eden", "ejigayehu", "elisabet", "emabet", "fatuma", "genet", "hanna", 
  "hirut", "konjit", "lemlem", "marta", "meseret", "mimi", "rahel", "selamawit", 
  "tigist", "tsehay", "yeshimebet", "yeshi", "zewdie", "zufan",

  "addisu", "amare", "andualem", "assefa", "ayele", "biruk", "dagmawi", "demeke",
  "endalkachew", "fasil", "feyisa", "gashaw", "habte", "henok", "kassa", "legesse",
  "mekbib", "meseret", "mulatu", "negussie", "samson", "shimelis", "tesfaye", "wondwossen",
  "yehuala", "yemane", "zerihun", "zinabu",

  "adanech", "adina", "azeb", "bethlehem", "bizunesh", "dagmawit", "deborah", "etenesh",
  "eyerusalem", "ferehiwot", "feven", "gelila", "hawani", "helina", "iman", "kalkidan",
  "kidist", "mahlet", "makeda", "menbere", "messay", "mihret", "nardos", "netsanet",
  "rediet", "selam", "senait", "tadelech", "tinsae", "tizita", "tsion", "winta", "yeabsira",
  "yordanos", "zenebech",

  "abate", "admasu", "asfaw", "asrat", "ayalew", "bayeh", "beyene", "bogale", "chala",
  "dagnachew", "debela", "demissie", "dereje", "desta", "dubale", "ejigu", "endale",
  "fantahun", "gebre", "gebremeskel", "gebremichael", "gebreyesus", "gelan", "gessesse",
  "habtamu", "hadgu", "kassahun", "mamo", "moges", "negash", "seifu", "shiferaw", "taddesse",
  "takele", "tefera", "tessema", "welde", "wolde", "yilma", "zelalem", "zerfu"
];

// Function to detect if a name is likely Ethiopian/Amharic
const isLikelyAmharicName = (name: string): boolean => {
  if (!name) return false;
  const normalizedName = name.toLowerCase().trim();
  
  return AMHARIC_NAME_PATTERNS.some(pattern => 
    normalizedName.includes(pattern)
  );
};

// Limited to just WhatsApp and Telegram
const MESSAGING_PLATFORMS = [
  "WhatsApp",
  "Telegram"
] as const;

// Function to get the appropriate country code prefix for a given country
const getCountryCodePrefix = (country: string): string => {
  const countryMap: Record<string, string> = {
    "Kenya": "+254",
    "Ethiopia": "+251",
    "Senegal": "+221",
    "Rwanda": "+250",
    "Nigeria": "+234",
    "South Africa": "+27",
    "Uganda": "+256",
    "Tanzania": "+255",
    "United States": "+1-US",
    "United Kingdom": "+44",
    "India": "+91",
    "UAE": "+971",
    "Australia": "+61",
    "Germany": "+49",
    "France": "+33",
    "China": "+86",
    "Brazil": "+55",
    "Mexico": "+52",
    "Japan": "+81",
    "Canada": "+1-CA",
    "Russia": "+7-RU",
    "Kazakhstan": "+7-KZ"
  };
  
  return countryMap[country] || ""; // Return empty string if country not found
};

// Function to get phone number example based on country code
const getPhoneNumberExample = (prefix: string): string => {
  const examples: Record<string, string> = {
    "+254": "712345678", // Kenya
    "+251": "911234567", // Ethiopia
    "+221": "771234567", // Senegal
    "+250": "781234567", // Rwanda
    "+234": "8012345678", // Nigeria
    "+27": "712345678", // South Africa
    "+256": "712345678", // Uganda
    "+255": "712345678", // Tanzania
    "+1-US": "2125551234", // US
    "+1-CA": "2125551234", // Canada
    "+44": "7123456789", // UK
    "+91": "9876543210", // India
    "+971": "501234567", // UAE
    "+61": "412345678", // Australia
    "+49": "15123456789", // Germany
    "+33": "612345678", // France
    "+86": "13123456789", // China
    "+55": "11987654321", // Brazil
    "+52": "5512345678", // Mexico
    "+81": "9012345678", // Japan
    "+7-RU": "9123456789", // Russia
    "+7-KZ": "7123456789" // Kazakhstan
  };
  
  return examples[prefix] || ""; // Return empty string if prefix not found
};

// Function to get country name from phone prefix
const getCountryFromPhonePrefix = (phoneNumber: string): string | null => {
  // Remove any non-digit characters except '+' from the phone number
  const cleanPhone = phoneNumber.replace(/[^\d+]/g, '');
  
  // Check common country codes
  if (cleanPhone.startsWith('+254')) return 'Kenya';
  if (cleanPhone.startsWith('+251')) return 'Ethiopia';
  if (cleanPhone.startsWith('+221')) return 'Senegal';
  if (cleanPhone.startsWith('+250')) return 'Rwanda';
  if (cleanPhone.startsWith('+234')) return 'Nigeria';
  if (cleanPhone.startsWith('+27')) return 'South Africa';
  if (cleanPhone.startsWith('+256')) return 'Uganda';
  if (cleanPhone.startsWith('+255')) return 'Tanzania';
  if (cleanPhone.startsWith('+1')) return 'United States'; // Default for +1
  if (cleanPhone.startsWith('+44')) return 'United Kingdom';
  if (cleanPhone.startsWith('+91')) return 'India';
  if (cleanPhone.startsWith('+971')) return 'UAE';
  if (cleanPhone.startsWith('+61')) return 'Australia';
  if (cleanPhone.startsWith('+49')) return 'Germany';
  if (cleanPhone.startsWith('+33')) return 'France';
  if (cleanPhone.startsWith('+86')) return 'China';
  if (cleanPhone.startsWith('+55')) return 'Brazil';
  if (cleanPhone.startsWith('+52')) return 'Mexico';
  if (cleanPhone.startsWith('+81')) return 'Japan';
  if (cleanPhone.startsWith('+7')) return 'Russia'; // Default for +7
  
  return null; // Return null if no match found
};

interface BasicInfoStepProps {
  onNext: () => void;
  onCancel: () => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

export const BasicInfoStep = ({ onNext, onCancel, isLoading, setIsLoading }: BasicInfoStepProps) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [profession, setProfession] = useState("");
  const [customProfession, setCustomProfession] = useState("");
  const [country, setCountry] = useState<string>("");
  const [preferredMessaging, setPreferredMessaging] = useState<string>("WhatsApp");
  const [phonePrefix, setPhonePrefix] = useState(""); // Start with empty prefix
  const [phoneNumber, setPhoneNumber] = useState("");
  const [telegramConnected, setTelegramConnected] = useState(false);
  const [searchQuery, setSearchQuery] = useState(""); // For searching country codes
  const [countrySearchQuery, setCountrySearchQuery] = useState(""); // For searching countries
  const [currency, setCurrency] = useState<string>("KES"); // Default currency
  const [profileImageUrl, setProfileImageUrl] = useState<string>("");
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const profileImageInputRef = useRef<HTMLInputElement>(null);
  const [showProfileCropper, setShowProfileCropper] = useState(false);
  const [tempProfileImageUrl, setTempProfileImageUrl] = useState<string | null>(null);
  const { toast } = useToast();
  const { t } = useTranslation();
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [telegramDeeplink, setTelegramDeeplink] = useState<string | null>(null);
  const [fetchingDeeplink, setFetchingDeeplink] = useState(false);
  
  // Filter countries based on search query
  const filteredCountries = countrySearchQuery
    ? COUNTRIES.filter(country => 
        country.toLowerCase().includes(countrySearchQuery.toLowerCase()))
    : COUNTRIES;

  // Filter phone prefixes based on search query
  const filteredPrefixes = searchQuery
    ? PHONE_PREFIXES.filter(prefix => 
        prefix.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : [...PHONE_PREFIXES].sort((a, b) => {
        // Sort by priority countries first, then alphabetically
        const priorityCountries = ["Kenya", "Ethiopia", "Senegal", "Rwanda", "Nigeria", "South Africa"];
        
        // Check if either is a priority country
        const aIsPriority = priorityCountries.some(country => a.label.includes(country));
        const bIsPriority = priorityCountries.some(country => b.label.includes(country));
        
        if (aIsPriority && !bIsPriority) return -1;
        if (!aIsPriority && bIsPriority) return 1;
        
        // If both are priority or both are not, sort alphabetically
        return a.label.localeCompare(b.label);
      });
  
  // Function to get appropriate currency for a country
  const getCountryCurrency = (country: string): string => {
    const normalized = country.trim();
    return currencyMap[normalized] || "USD"; // Default to USD if country not found
  };
  
  // Function to proactively fetch Telegram deeplink
  const fetchTelegramDeeplink = async () => {
    // Only fetch if user has selected Telegram and has a phone number
    if (preferredMessaging !== "Telegram" || !phoneNumber.trim() || !phonePrefix) {
      setTelegramDeeplink(null);
      return;
    }

    try {
      setFetchingDeeplink(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Process the phone prefix to remove country code suffixes if present
      let cleanPhonePrefix = phonePrefix;
      if (phonePrefix.includes("-")) {
        cleanPhonePrefix = phonePrefix.split("-")[0];
      }
      const fullPhoneNumber = `${cleanPhonePrefix}${phoneNumber}`;

      const { data, error } = await supabase.functions.invoke('telegram-bot', {
        body: {
          userId: user.id,
          action: "connect_telegram",
          timestamp: new Date().toISOString(),
          phoneNumber: fullPhoneNumber
        }
      });

      if (error) {
        console.error('Error fetching Telegram deeplink:', error);
        setTelegramDeeplink(null);
        return;
      }

      if (data.deeplink) {
        console.log('Proactively fetched Telegram deeplink:', data.deeplink);
        setTelegramDeeplink(data.deeplink);
      }
    } catch (error) {
      console.error('Error fetching Telegram deeplink:', error);
      setTelegramDeeplink(null);
    } finally {
      setFetchingDeeplink(false);
    }
  };
  
  // Update phone prefix based on country selection
  useEffect(() => {
    if (country) {
      setPhonePrefix(getCountryCodePrefix(country));
      // Set currency based on country
      const countryCurrency = getCountryCurrency(country);
      setCurrency(countryCurrency);
      console.log(`Country selected: ${country}, setting currency to: ${countryCurrency}`);
    }
  }, [country]);

  // Proactively fetch Telegram deeplink when messaging preference or phone details change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchTelegramDeeplink();
    }, 500); // Debounce to avoid too many API calls

    return () => clearTimeout(timeoutId);
  }, [preferredMessaging, phoneNumber, phonePrefix]);

  // Clean phone number by removing country code if it's already selected as prefix
  useEffect(() => {
    if (phonePrefix && phoneNumber) {
      // Remove the '+' from the prefix to match against the number
      const prefixDigits = phonePrefix.replace(/[^\d]/g, '');
      
      // Check if the phone number starts with the country code digits
      if (prefixDigits && phoneNumber.startsWith(prefixDigits)) {
        console.log(`Cleaning phone number: removing ${prefixDigits} from ${phoneNumber}`);
        // Remove the country code from the phone number
        setPhoneNumber(phoneNumber.substring(prefixDigits.length));
      }
    }
  }, [phonePrefix, phoneNumber]);

  // Load existing profile data if available
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const isGoogleAuth = user.app_metadata?.provider === 'google';
          const hasMetadata = !!user.user_metadata;

          // Check if user has a phone number from phone auth
          const userPhone = user.phone;
          
          // Fetch known existing columns to avoid errors
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('first_name, last_name, profession, preferred_messaging, telegram_chat_id, country, whatsapp_number, currency, profile_image_url')
            .eq('id', user.id)
            .single();

          if (error) {
            console.error("Error fetching profile:", error);
            return;
          }

          // --- FIRST NAME LOGIC ---
          if (profile?.first_name) {
            setFirstName(profile.first_name);
          } else if (isGoogleAuth && hasMetadata) {
            // Only use Google metadata for first name
            const googleFirstName =
              user.user_metadata?.given_name ||
              (user.user_metadata?.full_name ? user.user_metadata.full_name.split(' ')[0] : '') ||
              (user.user_metadata?.name ? user.user_metadata.name.split(' ')[0] : '');
            if (googleFirstName) {
              setFirstName(googleFirstName);
              await supabase.from('profiles').update({ first_name: googleFirstName }).eq('id', user.id);
            }
          } else if (user.email) {
            // Only use email as fallback if no Google metadata
            const emailName = user.email.split('@')[0];
            setFirstName(emailName);
            await supabase.from('profiles').update({ first_name: emailName }).eq('id', user.id);
          }

          // --- LAST NAME LOGIC ---
          if (profile?.last_name) {
            setLastName(profile.last_name);
          } else if (isGoogleAuth && hasMetadata) {
            const googleLastName =
              user.user_metadata?.family_name ||
              (user.user_metadata?.full_name ? user.user_metadata.full_name.split(' ').slice(1).join(' ') : '') ||
              (user.user_metadata?.name ? user.user_metadata.name.split(' ').slice(1).join(' ') : '');
            if (googleLastName) {
              setLastName(googleLastName);
              await supabase.from('profiles').update({ last_name: googleLastName }).eq('id', user.id);
            }
          }

          // --- PROFILE IMAGE LOGIC ---
          // Treat empty string as not set
          const profileImageIsSet = profile?.profile_image_url && profile.profile_image_url.trim() !== "";
          if (profileImageIsSet) {
            setProfileImageUrl(profile.profile_image_url!);
            console.log("Profile image loaded from DB:", profile.profile_image_url);
          } else if (isGoogleAuth && hasMetadata && user.user_metadata?.picture) {
            setProfileImageUrl(user.user_metadata.picture);
            console.log("Profile image loaded from Google metadata:", user.user_metadata.picture);
            // Save to DB only if not set
            await supabase.from('profiles').update({ profile_image_url: user.user_metadata.picture }).eq('id', user.id);
          } else {
            console.log("No profile image found in DB or Google metadata.");
          }

          if (profile?.profession) setProfession(profile.profession);

          // --- PHONE NUMBER LOGIC ---
          // First, check if we have a phone from auth (phone sign up)
          if (userPhone) {
            console.log("Phone found from auth:", userPhone);
            
            // Determine country from phone number
            const phoneCountry = getCountryFromPhonePrefix(userPhone);
            
            if (phoneCountry) {
              // Set the country based on phone prefix
              setCountry(phoneCountry);
              // Also set phone prefix based on country
              const prefix = getCountryCodePrefix(phoneCountry);
              setPhonePrefix(prefix);
              
              // Set the phone number without the prefix
              if (prefix && userPhone.startsWith(prefix)) {
                setPhoneNumber(userPhone.substring(prefix.length));
              } else if (userPhone.startsWith('+')) {
                // Try to extract the phone number without country code
                // Handle cases like +254712345678 → should extract 712345678
                const countryCodeMatch = userPhone.match(/^\+(\d{1,3})/);
                if (countryCodeMatch && countryCodeMatch[1]) {
                  // Remove country code (+XXX) from the phone number
                  setPhoneNumber(userPhone.substring(countryCodeMatch[0].length));
                } else {
                  // Fallback if we can't extract the prefix properly
                  setPhoneNumber(userPhone);
                }
              } else {
                // Fallback if the phone doesn't start with +
                setPhoneNumber(userPhone);
              }
              
              // Update whatsapp_number in profile if not already set
              if (!profile?.whatsapp_number) {
                await supabase.from('profiles').update({ 
                  whatsapp_number: userPhone,
                  country: phoneCountry
                }).eq('id', user.id);
              }
            } else {
              // If we can't determine country, try to parse the phone number
              if (userPhone.startsWith('+')) {
                // Extract country code and set prefix if possible
                const countryCodeMatch = userPhone.match(/^\+(\d{1,3})/);
                if (countryCodeMatch && countryCodeMatch[1]) {
                  // Find a matching country code prefix in our list
                  const countryCode = '+' + countryCodeMatch[1];
                  const matchingPrefix = PHONE_PREFIXES.find(p => 
                    p.value.startsWith(countryCode)
                  );
                  
                  if (matchingPrefix) {
                    setPhonePrefix(matchingPrefix.value);
                    // Remove country code from the phone number
                    setPhoneNumber(userPhone.substring(countryCodeMatch[0].length));
                  } else {
                    // If no match, just set the phone number as is
                    setPhoneNumber(userPhone);
                  }
                } else {
                  setPhoneNumber(userPhone);
                }
              } else {
                // Non-standard format, just set as is
                setPhoneNumber(userPhone);
              }
              
              // Update whatsapp_number in profile if not already set
              if (!profile?.whatsapp_number) {
                await supabase.from('profiles').update({ 
                  whatsapp_number: userPhone
                }).eq('id', user.id);
              }
            }
          }
          // If no phone from auth, fall back to what's in the profile
          else if (profile?.whatsapp_number) {
            const phoneWithPrefix = String(profile.whatsapp_number);
            const prefixes = PHONE_PREFIXES.map(p => p.value);

            // First try to find prefixes with country suffixes (like +1-US, +7-RU)
            let matchedPrefix = prefixes.find(prefix => phoneWithPrefix.startsWith(prefix));

            // If no match found and the phone number starts with +1 or +7, handle special cases
            if (!matchedPrefix) {
              if (phoneWithPrefix.startsWith("+1")) {
                // Default to +1-US if we can't determine specifically
                matchedPrefix = "+1-US";
              } else if (phoneWithPrefix.startsWith("+7")) {
                // Default to +7-RU if we can't determine specifically
                matchedPrefix = "+7-RU";
              }
            }

            if (matchedPrefix) {
              setPhonePrefix(matchedPrefix);
              setPhoneNumber(phoneWithPrefix.substring(matchedPrefix.length));
            } else {
              setPhoneNumber(phoneWithPrefix);
            }
          }

          // Set country with Kenya as default if not already set by phone logic
          if (profile?.country && !country) {
            setCountry(profile.country);
          } else if (!country) {
            setCountry("Kenya"); // Set a default country for new users
          }

          if (profile?.currency) setCurrency(profile.currency);

          // Set preferred messaging platform from DB if available, else default to WhatsApp
          if (profile?.preferred_messaging) {
            setPreferredMessaging(
              profile.preferred_messaging.charAt(0).toUpperCase() + profile.preferred_messaging.slice(1)
            );
          } else {
            setPreferredMessaging("WhatsApp");
          }

          // Check if user has telegram_chat_id, if so, they're connected
          if (profile?.telegram_chat_id) {
            setTelegramConnected(true);
          }
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };
    fetchUserProfile();
  }, []);
  
  // Function to connect to Telegram
  const connectTelegram = async () => {
    if (!phoneNumber) {
      toast({
        title: t('onboarding.phoneNumberRequired'),
        description: t('onboarding.phoneNumberRequiredDescription'),
        variant: "destructive"
      });
      return;
    }

    // Check if we have a pre-fetched deeplink
    if (!telegramDeeplink) {
      toast({
        title: "Telegram Connection",
        description: "Please wait while we prepare your Telegram connection...",
        variant: "default"
      });
      // Try to fetch the deeplink if we don't have it
      await fetchTelegramDeeplink();
      if (!telegramDeeplink) {
        alert("Failed to prepare Telegram connection. Please try again.");
        return;
      }
    }

    // Open Telegram window IMMEDIATELY to maintain user interaction chain
    console.log('Opening Telegram with pre-fetched deeplink:', telegramDeeplink);
    const telegramWindow = window.open(telegramDeeplink, '_blank');
    
    if (!telegramWindow || telegramWindow.closed || typeof telegramWindow.closed === 'undefined') {
      // Popup was blocked, use same-tab navigation as fallback
      console.log('Popup blocked, using same-tab navigation');
      window.location.href = telegramDeeplink;
      return;
    }

    // Show success message immediately since popup opened
    toast({
      title: "Telegram Connection",
      description: "Please check your browser for the Telegram bot link and complete the connection by sending /start",
    });

    // Now save all form fields to DB in the background
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');
      const selectedProf = profession === "Other" ? customProfession : profession;
      let cleanPhonePrefix = phonePrefix;
      if (phonePrefix.includes("-")) {
        cleanPhonePrefix = phonePrefix.split("-")[0];
      }
      const fullPhoneNumber = `${cleanPhonePrefix}${phoneNumber}`;
      const profileData: Record<string, any> = {
        first_name: firstName,
        last_name: lastName,
        profession: selectedProf,
        country: country,
        currency: currency,
        preferred_messaging: preferredMessaging.toLowerCase(),
        whatsapp_number: fullPhoneNumber,
        profile_image_url: profileImageUrl,
      };
      const { error: updateError } = await supabase
        .from('profiles')
        .update(profileData)
        .eq('id', user.id);
      
      if (updateError) {
        console.error("Profile update error after Telegram connect:", updateError);
        // Don't close the Telegram window on profile update error, just log it
        // The user can still connect to Telegram and we can update profile later
      }
    } catch (error) {
      console.error('Error saving profile after Telegram connect:', error);
      // Don't close the Telegram window on profile save error, just log it
      // The user can still connect to Telegram and we can update profile later
    }
  };

  // Handle profile image upload
  const handleProfileImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const imageUrl = URL.createObjectURL(file);
    setTempProfileImageUrl(imageUrl);
    setShowProfileCropper(true);
  };

  const handleAvatarClick = () => {
    profileImageInputRef.current?.click();
  };

  const handleDeleteProfileImage = () => {
    setProfileImageUrl("");
    setProfileImageFile(null);
    // Optionally, update DB to remove image
  };

  const handleCropComplete = (croppedImageUrl: string) => {
    setShowProfileCropper(false);
    setProfileImageUrl(croppedImageUrl);
    setProfileImageFile(null); // You may want to convert croppedImageUrl to a File if uploading
    setTempProfileImageUrl(null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Validate all required fields
      if (!firstName.trim()) {
        throw new Error("First name is required");
      }
      
      if (!lastName.trim()) {
        throw new Error("Last name is required");
      }
      
      if (!profession) {
        throw new Error("Please select your profession");
      }
      
      if (profession === "Other" && !customProfession.trim()) {
        throw new Error("Please specify your profession");
      }
      
      // Validate that country and phone prefix are selected
      if (!country) {
        throw new Error("Please select your country");
      }
      
      if (!phonePrefix) {
        throw new Error("Please select your phone country code");
      }
      
      if (!phoneNumber.trim()) {
        throw new Error("Phone number is required");
      }
      
      if (!preferredMessaging) {
        throw new Error("Please select your preferred messaging platform");
      }
      
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.error("Auth error:", userError);
        throw new Error("Authentication error. Please try again.");
      }
      
      const selectedProf = profession === "Other" ? customProfession : profession;
      
      // Process the phone prefix to remove country code suffixes if present
      let cleanPhonePrefix = phonePrefix;
      if (phonePrefix.includes("-")) {
        // Extract just the number part before the country suffix
        // e.g., +1-US becomes +1, +7-RU becomes +7
        cleanPhonePrefix = phonePrefix.split("-")[0];
      }
      
      const fullPhoneNumber = `${cleanPhonePrefix}${phoneNumber}`; // Combine prefix and number
      
      // Get UTM parameters from cookies (primary) with localStorage fallback
      const cookieParams = getUtmParamsFromCookies();
      const storedUtmParams = getStoredUtmParams();
      
      // Use cookies as primary source, fallback to stored params
      const utmParams = {
        utm_source: cookieParams.utm_source || storedUtmParams.utm_source,
        utm_medium: cookieParams.utm_medium || storedUtmParams.utm_medium,
        utm_campaign: cookieParams.utm_campaign || storedUtmParams.utm_campaign,
        utm_content: cookieParams.utm_content || storedUtmParams.utm_content,
        utm_term: cookieParams.utm_term || storedUtmParams.utm_term,
        referrer: cookieParams.referrer || storedUtmParams.referrer
      };
      
      console.log('UTM parameters for profile update:', {
        fromCookies: cookieParams,
        fromStored: storedUtmParams,
        final: utmParams
      });
      
      // Prepare profile update data
      const profileData: Record<string, any> = {
        profession: selectedProf,
        service_type: "Professional Services", // Default service type
        company_name: "", // Leave company name blank instead of using first/last name
        first_name: firstName,
        last_name: lastName,
        country: country,
        currency: currency, // Save currency preference
        preferred_messaging: preferredMessaging.toLowerCase(), // Save user's choice
        whatsapp_number: fullPhoneNumber, // Store in whatsapp_number column regardless of messaging platform
        service_page_link: `services/${user.id}`, // Ensure service_page_link is set properly
        // Include UTM parameters from cookies (primary) with fallbacks
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        utm_content: utmParams.utm_content,
        utm_term: utmParams.utm_term,
        referrer: utmParams.referrer
      };
      
      console.log('Saving profile with UTM parameters from cookies:', {
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        utm_content: utmParams.utm_content,
        utm_term: utmParams.utm_term,
        referrer: utmParams.referrer
      });
      
      // Update user profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update(profileData)
        .eq('id', user.id);
        
      if (updateError) {
        console.error("Profile update error:", updateError);
        throw new Error("Failed to update profile. Please try again.");
      }
      
      console.log('Profile updated successfully with cookie-sourced UTM parameters');
      
      // Clear UTM parameters from localStorage after successful profile update
      clearUtmParams();
      console.log('Cleared UTM parameters from localStorage after profile update');
      
      // Move to next step
      onNext();
    } catch (error) {
      console.error("Error during profile update:", error);
      alert(error instanceof Error ? error.message : "An error occurred during profile update");
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to check if Next should be disabled
  const isNextDisabled =
    isLoading ||
    !firstName.trim() ||
    !lastName.trim() ||
    !profession ||
    !country ||
    !currency ||
    !phonePrefix ||
    !phoneNumber.trim() ||
    !preferredMessaging ||
    (preferredMessaging === "Telegram" && !telegramConnected);

  // Helper to get missing fields
  const getMissingFields = () => {
    const fields = [];
    if (!firstName) fields.push(t('onboarding.firstName'));
    if (!lastName) fields.push(t('onboarding.lastName'));
    if (!profession) fields.push(t('onboarding.whatIsYourProfession'));
    if (!country) fields.push(t('onboarding.country'));
    if (!currency) fields.push(t('onboarding.currency'));
    if (!preferredMessaging) fields.push(t('onboarding.messagingPlatform'));
    if (!phonePrefix) fields.push(t('onboarding.phoneNumber'));
    if (!phoneNumber) fields.push(t('onboarding.phoneNumber'));
    if (preferredMessaging === "Telegram" && !telegramConnected) fields.push("Telegram Connection");
    return fields;
  };

  // Handler for clicking the disabled Next button
  const handleDisabledNextClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const fields = getMissingFields();
    toast({
      title: t('onboarding.pleaseComplete'),
      description: fields.join(", "),
      variant: "destructive"
    });
  };

  return (
    <div>
      <div className="text-center mb-4">
        <p className="text-lg font-medium">{t('onboarding.step1of2')}</p>
      </div>
      
      <h2 className="text-2xl font-bold mb-6 text-center bg-gradient-to-r from-gebeya-pink to-gebeya-orange bg-clip-text text-transparent">
        {t('onboarding.tellUsAboutYourself')}
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Profile Picture Field */}
        <div className="space-y-2 flex flex-col items-center">
          <Label htmlFor="profileImage">{t('onboarding.profilePicture')}</Label>
          <div className="flex flex-col items-center space-y-3 p-4 border rounded-md">
            <div className="relative">
              <Avatar
                className="w-24 h-24 cursor-pointer hover:opacity-90 transition-opacity"
                onClick={handleAvatarClick}
              >
                {profileImageUrl ? (
                  <AvatarImage src={profileImageUrl} alt="Profile" />
                ) : (
                  <AvatarFallback style={{ background: "linear-gradient(to right, #FF1493, #FFA500)" }} className="text-white w-24 h-24 flex items-center justify-center">
                    <ImageIcon className="w-12 h-12 text-white" />
                  </AvatarFallback>
                )}
              </Avatar>
              {profileImageUrl && (
                <button
                  onClick={handleDeleteProfileImage}
                  className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                  title="Delete image"
                  type="button"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={handleAvatarClick}
              className="mt-2"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t('onboarding.uploadPicture')}
            </Button>
            <input
              id="profileImage"
              name="profileImage"
              type="file"
              ref={profileImageInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleProfileImageChange}
            />
          </div>
          {showProfileCropper && tempProfileImageUrl && (
            <ProfileImageCropper
              open={showProfileCropper}
              onClose={() => {
                setShowProfileCropper(false);
                setTempProfileImageUrl(null);
              }}
              imageUrl={tempProfileImageUrl}
              onCropComplete={handleCropComplete}
            />
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">{t('onboarding.firstName')} <span className="text-red-500">*</span></Label>
            <Input
              id="firstName"
              name="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              placeholder={t('onboarding.yourFirstName')}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="lastName">{t('onboarding.lastName')} <span className="text-red-500">*</span></Label>
            <Input
              id="lastName"
              name="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              placeholder={t('onboarding.yourLastName')}
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="profession">{t('onboarding.whatIsYourProfession')} <span className="text-red-500">*</span></Label>
          <Select
            value={profession}
            onValueChange={setProfession}
            required
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('onboarding.selectYourProfession')} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>{t('onboarding.professions')}</SelectLabel>
                {PROFESSIONS.map((prof) => (
                  <SelectItem key={prof} value={prof}>
                    {prof}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          
          {profession === "Other" && (
            <div className="mt-2">
              <Input
                placeholder={t('onboarding.enterYourProfession')}
                value={customProfession}
                onChange={(e) => setCustomProfession(e.target.value)}
                required
              />
            </div>
          )}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="country">{t('onboarding.country')} <span className="text-red-500">*</span></Label>
          <Select
            value={country}
            onValueChange={setCountry}
            required
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('onboarding.selectYourCountry')} />
            </SelectTrigger>
            <SelectContent className="max-h-[300px] overflow-y-auto">
              <div className="px-2 py-2">
                <Input
                  placeholder={t('onboarding.searchCountries')}
                  className="mb-2"
                  value={countrySearchQuery}
                  onChange={(e) => setCountrySearchQuery(e.target.value)}
                  autoComplete="off"
                />
              </div>
              {filteredCountries.length === 0 ? (
                <div className="px-2 py-4 text-center text-sm text-gray-500">
                  {t('onboarding.noCountriesFound')}
                </div>
              ) : (
                <SelectGroup>
                  <SelectLabel>{t('onboarding.countries')}</SelectLabel>
                  {filteredCountries.map((country) => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
        </div>
        
        {/* Currency Selection - Now read-only as it's set by country */}
        <div className="space-y-2">
          <Label htmlFor="currency">{t('onboarding.currency')} <span className="text-red-500">*</span></Label>
          <Select
            value={currency}
            onValueChange={setCurrency}
            required
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('onboarding.selectYourCurrency')} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>{t('onboarding.africanCurrencies')}</SelectLabel>
                <SelectItem value="KES">KES (Kenyan Shilling)</SelectItem>
                <SelectItem value="ETB">ETB (Ethiopian Birr)</SelectItem>
                <SelectItem value="NGN">NGN (Nigerian Naira)</SelectItem>
                <SelectItem value="ZAR">ZAR (South African Rand)</SelectItem>
                <SelectItem value="GHS">GHS (Ghanaian Cedi)</SelectItem>
                <SelectItem value="TZS">TZS (Tanzanian Shilling)</SelectItem>
                <SelectItem value="RWF">RWF (Rwandan Franc)</SelectItem>
                <SelectItem value="UGX">UGX (Ugandan Shilling)</SelectItem>
                <SelectItem value="XOF">XOF (West African CFA Franc)</SelectItem>
                <SelectItem value="DZD">DZD (Algerian Dinar)</SelectItem>
                <SelectItem value="AOA">AOA (Angolan Kwanza)</SelectItem>
                <SelectItem value="BWP">BWP (Botswanan Pula)</SelectItem>
                <SelectItem value="BIF">BIF (Burundian Franc)</SelectItem>
                <SelectItem value="CVE">CVE (Cape Verdean Escudo)</SelectItem>
                <SelectItem value="XAF">XAF (Central African CFA Franc)</SelectItem>
                <SelectItem value="KMF">KMF (Comorian Franc)</SelectItem>
                <SelectItem value="CDF">CDF (Congolese Franc)</SelectItem>
                <SelectItem value="DJF">DJF (Djiboutian Franc)</SelectItem>
                <SelectItem value="EGP">EGP (Egyptian Pound)</SelectItem>
                <SelectItem value="ERN">ERN (Eritrean Nakfa)</SelectItem>
                <SelectItem value="SZL">SZL (Swazi Lilangeni)</SelectItem>
                <SelectItem value="GMD">GMD (Gambian Dalasi)</SelectItem>
                <SelectItem value="GNF">GNF (Guinean Franc)</SelectItem>
                <SelectItem value="LSL">LSL (Lesotho Loti)</SelectItem>
                <SelectItem value="LRD">LRD (Liberian Dollar)</SelectItem>
                <SelectItem value="LYD">LYD (Libyan Dinar)</SelectItem>
                <SelectItem value="MGA">MGA (Malagasy Ariary)</SelectItem>
                <SelectItem value="MWK">MWK (Malawian Kwacha)</SelectItem>
                <SelectItem value="MRU">MRU (Mauritanian Ouguiya)</SelectItem>
                <SelectItem value="MUR">MUR (Mauritian Rupee)</SelectItem>
                <SelectItem value="MAD">MAD (Moroccan Dirham)</SelectItem>
                <SelectItem value="MZN">MZN (Mozambican Metical)</SelectItem>
                <SelectItem value="NAD">NAD (Namibian Dollar)</SelectItem>
                <SelectItem value="STN">STN (São Tomé and Príncipe Dobra)</SelectItem>
                <SelectItem value="SCR">SCR (Seychellois Rupee)</SelectItem>
                <SelectItem value="SLL">SLL (Sierra Leonean Leone)</SelectItem>
                <SelectItem value="SOS">SOS (Somali Shilling)</SelectItem>
                <SelectItem value="SSP">SSP (South Sudanese Pound)</SelectItem>
                <SelectItem value="SDG">SDG (Sudanese Pound)</SelectItem>
                <SelectItem value="TND">TND (Tunisian Dinar)</SelectItem>
                <SelectItem value="ZMW">ZMW (Zambian Kwacha)</SelectItem>
                <SelectItem value="ZWL">ZWL (Zimbabwean Dollar)</SelectItem>
              </SelectGroup>
              <SelectGroup>
                <SelectLabel>{t('onboarding.globalCurrencies')}</SelectLabel>
                <SelectItem value="USD">USD (US Dollar)</SelectItem>
                <SelectItem value="EUR">EUR (Euro)</SelectItem>
                <SelectItem value="GBP">GBP (British Pound)</SelectItem>
                <SelectItem value="CAD">CAD (Canadian Dollar)</SelectItem>
                <SelectItem value="AUD">AUD (Australian Dollar)</SelectItem>
                <SelectItem value="JPY">JPY (Japanese Yen)</SelectItem>
                <SelectItem value="CHF">CHF (Swiss Franc)</SelectItem>
                <SelectItem value="CNY">CNY (Chinese Yuan)</SelectItem>
                <SelectItem value="INR">INR (Indian Rupee)</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500">
            Currency automatically set based on country
          </p>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="preferredMessaging">{t('onboarding.messagingPlatform')} <span className="text-red-500">*</span></Label>
          <div className="flex justify-center">
            <RadioGroup
              value={preferredMessaging}
              onValueChange={setPreferredMessaging}
              className="flex space-x-8"
              required
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="WhatsApp" id="whatsapp" />
                <Label htmlFor="whatsapp" className="cursor-pointer flex items-center space-x-2">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#25D366]">
                    <img src="/icons8-whatsapp.svg" alt="WhatsApp" width={20} height={20} className="inline-block" style={{ filter: 'brightness(0) invert(1)' }} />
                  </span>
                  <span>WhatsApp</span>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Telegram" id="telegram" />
                <Label htmlFor="telegram" className="cursor-pointer flex items-center space-x-2">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0088cc]">
                    <img src="/icons8-telegram.svg" alt="Telegram" width={20} height={20} className="inline-block" style={{ filter: 'brightness(0) invert(1)' }} />
                  </span>
                  <span>Telegram</span>
                </Label>
              </div>
            </RadioGroup>
          </div>
          <p className="text-xs text-gray-500 text-center">{t('onboarding.choosePlatformDescription')}</p>
        </div>
        
        {/* Phone number field with country code prefix */}
        <div className="space-y-2">
          <Label htmlFor="phoneNumber">{t('onboarding.phoneNumber')} <span className="text-red-500">*</span></Label>
          <div className="flex">
            <Select 
              value={phonePrefix}
              onValueChange={setPhonePrefix}
              required
            >
              <SelectTrigger className="w-[140px] rounded-r-none">
                <SelectValue placeholder={t('onboarding.selectCode')} />
              </SelectTrigger>
              <SelectContent className="max-h-[300px] overflow-y-auto">
                <div className="px-2 py-2">
                  <Input
                    placeholder={t('onboarding.searchCountryCode')}
                    className="mb-2"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                {filteredPrefixes.length === 0 ? (
                  <div className="px-2 py-4 text-center text-sm text-gray-500">
                    {t('onboarding.noCountryCodesFound')}
                  </div>
                ) : (
                  <SelectGroup>
                    {!searchQuery && (
                      <SelectLabel className="px-2 py-1.5 text-xs font-semibold text-gray-500">
                        {t('onboarding.popular')}
                      </SelectLabel>
                    )}
                    {filteredPrefixes.map((prefix, index) => {
                      // Show divider after priority countries if not searching
                      const showDivider = !searchQuery && index === 5;
                      
                      return (
                        <div key={prefix.value}>
                          {showDivider && (
                            <SelectLabel className="px-2 py-1.5 text-xs font-semibold text-gray-500 mt-2">
                              {t('onboarding.allCountries')}
                            </SelectLabel>
                          )}
                          <SelectItem value={prefix.value}>
                            {prefix.label}
                          </SelectItem>
                        </div>
                      );
                    })}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
            <Input
              id="phoneNumber"
              name="phoneNumber"
              type="tel"
              className="rounded-l-none placeholder:text-gray-300"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
              required
              placeholder={getPhoneNumberExample(phonePrefix)}
            />
          </div>
          <p className="text-xs text-gray-500">{t('onboarding.enterOnlyDigits')}</p>
        </div>
        
        {/* Conditionally show Telegram connection */}
        {preferredMessaging === "Telegram" && !telegramConnected && (
          <div className="p-4 border border-gebeya-pink/20 rounded-lg bg-gebeya-pink/5">
            <h3 className="font-medium mb-2">{t('onboarding.connectToTelegram')}</h3>
            <p className="text-sm text-gray-600 mb-3">
              <span className="font-semibold text-gebeya-pink">{t('onboarding.youMustConnect')}</span><br />
              {t('onboarding.telegramConnectionDescription')} <br />
              <span className="text-gray-700">{t('onboarding.telegramConnectionWarning')}</span>
            </p>
            <div className="relative w-fit flex justify-center mx-auto">
              <Button 
                type="button" 
                className="bg-[#0088cc] hover:bg-[#0077b5] text-white disabled:opacity-50"
                onClick={connectTelegram}
                disabled={!phoneNumber || fetchingDeeplink}
              >
                {fetchingDeeplink ? t('onboarding.preparingConnection') : t('onboarding.connectTelegram')}
              </Button>
              {!phoneNumber && (
                <div
                  className="absolute inset-0 cursor-not-allowed z-10"
                  onClick={() => toast({
                    title: t('onboarding.phoneNumberRequired'),
                    description: t('onboarding.phoneNumberRequiredDescription'),
                    variant: "destructive"
                  })}
                  style={{ background: "rgba(255,255,255,0)", borderRadius: 8 }}
                />
              )}
            </div>
          </div>
        )}
        
        {preferredMessaging === "Telegram" && telegramConnected && (
          <div className="p-4 border border-green-100 rounded-lg bg-green-50">
            <h3 className="font-medium mb-2 text-green-800">{t('onboarding.telegramConnected')}</h3>
            <p className="text-sm text-green-700">
              {t('onboarding.telegramConnectedDescription')}
            </p>
          </div>
        )}
        
        <div className="flex gap-4 justify-end mt-8 relative">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
          >
            {t('onboarding.cancel')}
          </Button>
          <div className="relative">
            <Button
              type="submit"
              disabled={isNextDisabled}
              className="bg-gradient-to-r from-gebeya-pink to-gebeya-orange text-white hover:opacity-90"
            >
              {isLoading ? t('onboarding.saving') : t('onboarding.next')}
            </Button>
            {isNextDisabled && (
              <div
                className="absolute inset-0 cursor-not-allowed z-10"
                onClick={handleDisabledNextClick}
                style={{ background: "rgba(255,255,255,0)", borderRadius: 8 }}
              />
            )}
          </div>
        </div>
        {missingFields.length > 0 && (
          <div className="text-red-600 text-sm mt-2 text-center">
            Please complete: {missingFields.join(", ")}
          </div>
        )}
      </form>
    </div>
  );
}; 